import { createUserPreRegistration, updateUserPreRegistration } from '@/services/api/UserPreRegistration';

class UserPreRegistration {
  constructor() {
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
    this.queue = [];
    this.processing = false;
    this.currentRegistrationId = null;
  }

  /**
   * Save pre-registration data (API handles create/update automatically)
   * @param {Object} userData - User contact data
   * @param {string} userData.full_name - User's full name
   * @param {string} userData.email - User's email address
   * @param {string} userData.phone_number - User's phone number
   * @param {Object} userData.steps - JSON object containing the steps/questions answered
   * @returns {Promise<number>} - Returns the registration ID
   */
  async save(userData) {
    try {
      // Validate required fields
      const requiredFields = ['full_name', 'email', 'phone_number'];
      for (const field of requiredFields) {
        if (!userData[field]) {
          console.warn(`UserPreRegistration: Missing required field '${field}'`);
          return null;
        }
      }

      // Ensure steps is an object
      if (!userData.steps || typeof userData.steps !== 'object') {
        userData.steps = {};
      }

      // Add to queue for processing
      this.queue.push({
        action: 'save',
        data: {
          ...userData,
          timestamp: new Date().toISOString(),
        },
        attempts: 0
      });

      // Process queue if not already processing
      if (!this.processing) {
        await this.processQueue();
      }

      return this.currentRegistrationId;
    } catch (error) {
      console.error('UserPreRegistration: Error saving registration:', error);
      return null;
    }
  }

  /**
   * Process the queue
   */
  async processQueue() {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;

    while (this.queue.length > 0) {
      const item = this.queue.shift();
      await this.sendRequest(item);
    }

    this.processing = false;
  }

  /**
   * Send request to the API
   * @param {Object} item - Queue item
   */
  async sendRequest(item) {
    try {
      // Always use createUserPreRegistration - API handles create/update logic
      const response = await createUserPreRegistration(item.data, {
        timeout: 5000, // 5 second timeout
      });

      // Store the registration ID for future updates
      if (response.status === 200 || response.status === 201) {
        this.currentRegistrationId = response.data?.data?.id || response.data?.id;
        console.log('UserPreRegistration: Saved successfully', {
          id: this.currentRegistrationId,
          email: item.data.email,
          action: item.action
        });
      }
    } catch (error) {
      // Handle errors gracefully without affecting the main app
      this.handleError(error, item);
    }
  }

  /**
   * Handle errors with retry logic
   * @param {Error} error - The error that occurred
   * @param {Object} item - The item that failed
   */
  handleError(error, item) {
    item.attempts = (item.attempts || 0) + 1;

    if (item.attempts < this.retryAttempts) {
      // Retry after delay
      setTimeout(() => {
        this.queue.unshift(item); // Add back to front of queue
        if (!this.processing) {
          this.processQueue();
        }
      }, this.retryDelay * item.attempts); // Exponential backoff

      console.warn(`UserPreRegistration: Retrying request (attempt ${item.attempts}/${this.retryAttempts})`, {
        action: item.action,
        error: error.message
      });
    } else {
      // Max retries reached - log error but don't crash the app
      console.error('UserPreRegistration: Failed after maximum retries', {
        action: item.action,
        error: error.message,
        response: error.response?.data
      });
    }
  }

  /**
   * Get current registration ID
   */
  getCurrentRegistrationId() {
    return this.currentRegistrationId;
  }

  /**
   * Set registration ID manually (useful when resuming a session)
   */
  setRegistrationId(id) {
    this.currentRegistrationId = id;
  }

  /**
   * Clear the registration ID
   */
  clearRegistrationId() {
    this.currentRegistrationId = null;
  }

  /**
   * Get current queue size
   */
  getQueueSize() {
    return this.queue.length;
  }

  /**
   * Clear the queue
   */
  clearQueue() {
    this.queue = [];
    console.log('UserPreRegistration: Queue cleared');
  }
}

// Create singleton instance
const userPreRegistration = new UserPreRegistration();

export default userPreRegistration;
